import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  UserPlus,
  BookMarked,
  BarChart3
} from 'lucide-react';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useDatabaseCourseNavigation } from '@/hooks/useDatabaseCourseNavigation';
import { CourseCountBadge } from '@/components/common/CourseCountBadge';
import CourseStatusChart from './CourseStatusChart';
import TopCoursesChart from './TopCoursesChart';
import ProgressTrendChart from './ProgressTrendChart';
import { staffAnalyticsDataService, type CourseStatusData, type TopCourse, type ProgressTrendData } from '@/services/staffAnalyticsDataService';

interface StaffCourseStats {
  assignedStudents: number;
  activeCourses: number;
  completedCourses: number;
  averageProgress: number;
}

interface StudentProgress {
  id: string;
  user_id: string;
  course_id: string;
  created_at: string;
  assignment_status?: string;
  final_grade?: number;
  progress_percentage?: number;
  last_activity?: string;
  user_profiles?: {
    display_name?: string;
    email?: string;
  };
}

const StaffCourseOverview: React.FC = () => {
  const navigate = useNavigate();
  const { hasAccess } = useSupabaseAuth();
  const { toast } = useToast();
  const { categories: databaseCategories } = useDatabaseCourseNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StaffCourseStats>({
    assignedStudents: 0,
    activeCourses: 0,
    completedCourses: 0,
    averageProgress: 0
  });
  const [studentProgress, setStudentProgress] = useState<StudentProgress[]>([]);
  const [courseStatusData, setCourseStatusData] = useState<CourseStatusData>({
    notStarted: 0,
    inProgress: 0,
    completed: 0
  });
  const [topCoursesData, setTopCoursesData] = useState<TopCourse[]>([]);
  const [progressTrendData, setProgressTrendData] = useState<ProgressTrendData[]>([]);

  useEffect(() => {
    if (hasAccess('staff')) {
      fetchStaffStats();
      fetchStudentProgress();
      fetchChartData();
    }
  }, [hasAccess]);

  const fetchChartData = async () => {
    try {
      const [courseStatus, topCourses, progressTrend] = await Promise.all([
        staffAnalyticsDataService.getCourseStatusDistribution(),
        staffAnalyticsDataService.getTopCourses(5),
        staffAnalyticsDataService.getProgressTrend(30)
      ]);

      setCourseStatusData(courseStatus);
      setTopCoursesData(topCourses);
      setProgressTrendData(progressTrend);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchStaffStats = async () => {
    try {
      setLoading(true);
      
      // Get real stats from database function
      const { data: statsData, error } = await supabase.rpc('get_staff_course_overview_stats');
      
      if (error) throw error;
      
      const stats = statsData as any;
      if (stats?.error) {
        throw new Error(stats.error);
      }

      setStats({
        assignedStudents: stats.assigned_students || 0,
        activeCourses: stats.active_courses || 0,
        completedCourses: stats.completed_courses || 0,
        averageProgress: stats.average_progress || 0
      });

    } catch (error) {
      console.error('Error fetching staff stats:', error);
      toast({
        title: "Error",
        description: "Failed to fetch statistics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentProgress = async () => {
    try {
      const { data, error } = await supabase.rpc('get_staff_student_progress');

      if (error) throw error;
      
      // Transform data to match component interface
      const enrichedData = (data || []).map(item => ({
        id: item.id,
        user_id: item.user_id,
        course_id: item.course_id,
        created_at: item.created_at,
        assignment_status: item.assignment_status,
        final_grade: item.final_grade,
        user_profiles: {
          display_name: item.user_name || 'Unknown User',
          email: item.user_email || 'No email'
        },
        progress_percentage: item.progress_percentage || 0,
        last_activity: item.last_activity
      }));
      
      setStudentProgress(enrichedData as StudentProgress[]);
    } catch (error) {
      console.error('Error fetching student progress:', error);
    }
  };

  const filteredProgress = studentProgress.filter(progress =>
    progress.course_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    progress.user_profiles?.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    progress.user_profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statsCards = [
    {
      title: 'Assigned Students',
      value: stats.assignedStudents,
      icon: Users,
      color: 'text-blue-600'
    },
    {
      title: 'Active Courses',
      value: stats.activeCourses,
      icon: BookOpen,
      color: 'text-green-600'
    },
    {
      title: 'Completed Courses',
      value: stats.completedCourses,
      icon: CheckCircle,
      color: 'text-purple-600'
    },
    {
      title: 'Average Progress',
      value: `${stats.averageProgress}%`,
      icon: TrendingUp,
      color: 'text-orange-600'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress':
      case 'assigned':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Course Overview</h2>
          <p className="text-muted-foreground">
            Monitor student progress and course engagement
          </p>
          <CourseCountBadge showIcons={true} className="mt-2" />
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Visual Analytics Section */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Course Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CourseStatusChart
              notStarted={courseStatusData.notStarted}
              inProgress={courseStatusData.inProgress}
              completed={courseStatusData.completed}
            />
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookMarked className="h-5 w-5" />
              Top 5 Courses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TopCoursesChart data={topCoursesData} />
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Progress Trend (30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProgressTrendChart data={progressTrendData} />
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate('/staff/users')}
            >
              <Users className="h-4 w-4 mr-2" />
              Manage Users
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate('/staff/users?action=create')}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Create Student
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate('/courses')}
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Browse Courses
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate('/staff?tab=reports')}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              View Reports
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search students or courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" className="gap-2">
          <Filter className="h-4 w-4" />
          Filter
        </Button>
      </div>

      {/* Student Progress List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Student Progress Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredProgress.map((progress) => (
              <div 
                key={progress.id} 
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-medium">
                        {progress.user_profiles?.display_name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {progress.user_profiles?.email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Course:</span>{' '}
                      <span className="font-medium">{progress.course_id}</span>
                    </div>
                    <Badge variant="outline">
                      General
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(progress.assignment_status || 'assigned')}
                      <span className="text-sm capitalize">
                        {progress.assignment_status?.replace('_', ' ') || 'Assigned'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-1 max-w-xs">
                      <Progress 
                        value={progress.progress_percentage || 0} 
                        className="flex-1"
                      />
                      <span className={`text-sm font-medium ${getProgressColor(progress.progress_percentage || 0)}`}>
                        {Math.round(progress.progress_percentage || 0)}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    Last accessed: {progress.last_activity ? 
                      new Date(progress.last_activity).toLocaleDateString() : 
                      new Date(progress.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                  <Button variant="outline" size="sm">
                    Message Student
                  </Button>
                </div>
              </div>
            ))}

            {filteredProgress.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'No matching results found.' : 'No student progress data available.'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Course Categories Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Available Course Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {databaseCategories.slice(0, 9).map((category) => (
              <div 
                key={category.id} 
                onClick={() => navigate(category.route)}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 hover:shadow-md transition-all duration-200"
              >
                <category.icon className="h-6 w-6 text-primary" />
                <div>
                  <div className="font-medium">{category.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {category.courses.length} courses
                  </div>
                </div>
              </div>
            ))}
          </div>
          {databaseCategories.length > 9 && (
            <div className="text-center mt-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/courses')}
              >
                View All {databaseCategories.length} Categories
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffCourseOverview;